import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Check, Loader2, Reply } from "lucide-react"
import { useState } from "react"
import {
  useAnswerSupplierMessage,
  useMarkSupplierMessageDone,
  type InquirySupplierMessage,
} from "../../../api/supplierInquiriesApi"
import { McPill, type McPillTone } from "../../shared/McPill"
import { CollapsibleBody } from "./InquiryEmailSheet"

const STATE_PILL: Partial<
  Record<
    InquirySupplierMessage["state"],
    { label: TranslationKeys; tone: McPillTone }
  >
> = {
  open: { label: "MC_INQ_OPEN_MESSAGE_PILL", tone: "attention" },
  answered: { label: "MC_INQ_MESSAGE_ANSWERED_PILL", tone: "success" },
  done: { label: "MC_INQ_MESSAGE_DONE_PILL", tone: "muted" },
}

interface SupplierMessageBlockProps {
  inquiryId: string
  message: InquirySupplierMessage
  /** The From of the reply: the answer goes back to this address. */
  sender: string | null
}

/** A supplier reply's own words on the timeline row, with what is left to do about it
 *  (TUR-1754): answer it on the inquiry's thread, or mark it done. */
export function SupplierMessageBlock({
  inquiryId,
  message,
  sender,
}: SupplierMessageBlockProps) {
  const { translate } = useTranslation()
  const answer = useAnswerSupplierMessage()
  const markDone = useMarkSupplierMessageDone()
  const [composerOpen, setComposerOpen] = useState(false)
  const [body, setBody] = useState("")

  const pill = STATE_PILL[message.state]
  const settled = message.state === "answered" || message.state === "done"
  const canAnswer = !settled && answer.canRun
  const canMarkDone = message.state === "open" && markDone.canRun

  const send = () =>
    answer.mutate(
      { inquiryId, messageId: message.id, body: body.trim() },
      {
        onSuccess: () => {
          setComposerOpen(false)
          setBody("")
        },
      },
    )

  return (
    <div className="mt-1.5">
      {message.text && (
        <div className="border-l-2 border-grey4 pl-3 text-grey4">
          <CollapsibleBody text={message.text} />
        </div>
      )}
      {(pill || canAnswer || canMarkDone) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {pill && <McPill tone={pill.tone}>{translate(pill.label)}</McPill>}
          {canAnswer && (
            <button
              type="button"
              onClick={() => setComposerOpen(true)}
              className="inline-flex items-center gap-1 text-yellow1 hover:underline"
            >
              <Reply className="h-3 w-3" />
              {translate("MC_INQ_MESSAGE_ANSWER")}
            </button>
          )}
          {canMarkDone && (
            <button
              type="button"
              onClick={() =>
                markDone.mutate({ inquiryId, messageId: message.id })
              }
              disabled={markDone.isPending}
              className="inline-flex items-center gap-1 text-yellow1 hover:underline disabled:opacity-50"
            >
              <Check className="h-3 w-3" />
              {translate("MC_INQ_MESSAGE_MARK_DONE")}
            </button>
          )}
        </div>
      )}

      <Dialog
        open={composerOpen}
        onOpenChange={(open) => {
          if (!answer.isPending) setComposerOpen(open)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate("MC_INQ_MESSAGE_ANSWER_TITLE")}
            </DialogTitle>
            <DialogDescription>
              {translate("MC_INQ_MESSAGE_ANSWER_DESC", {
                to: sender ?? "—",
              })}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            maxLength={10000}
            autoFocus
            aria-label={translate("MC_INQ_MESSAGE_ANSWER_TITLE")}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setComposerOpen(false)}
              disabled={answer.isPending}
            >
              {translate("MC_DECL_BTN_CANCEL")}
            </Button>
            <Button
              onClick={send}
              disabled={answer.isPending || body.trim().length === 0}
            >
              {answer.isPending && (
                <Loader2 size={14} className="animate-spin" />
              )}
              {translate("MC_INQ_MESSAGE_ANSWER_SEND")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
